import { localizeApplicantNotification } from '../../utils/localizedSystemMessages';
import React, { useState, useEffect } from 'react';
import './NotificationsPopup.css';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import ApprovalNotificationItem from '../Approvals/ApprovalNotificationItem';

const NotificationsPopup = ({
  isOpen,
  onClose,
  notifications = [],
  onSelectNotification,
  pendingApprovals = [],
  companyUID,
  onApprovalComplete,
  onViewApprovalDetails
}) => {
  const { t } = useTranslation('applicants');
  const { isRTL } = useLanguage();

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    // Close when clicking on the semi-transparent background
    if (e.target.classList.contains('notifications-overlay')) {
      onClose();
    }
  };

  const handleSelect = (notification) => {
    if (onSelectNotification) {
      onSelectNotification(notification);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'unread_chat':
        return 'fa-solid fa-message';
      case 'assessment_completed':
        return 'fa-solid fa-clipboard-check';
      case 'unmoved_candidate':
        return 'fa-solid fa-clock';
      case 'checklist_reminder':
        return 'fa-solid fa-list-check';
      default:
        return 'fa-solid fa-bell';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'unread_chat':
        return '#568ded';
      case 'assessment_completed':
        return '#28a745';
      case 'unmoved_candidate':
        return '#ffc107';
      case 'checklist_reminder':
        return '#dc3545';
      default:
        return '#568ded';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';

    const now = new Date();
    const notificationTime = new Date(parseInt(timestamp)); // Parse the numeric timestamp
    const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));

    if (diffInMinutes < 1) {
      return t('notifications.time.justNow');
    } else if (diffInMinutes < 60) {
      return t('notifications.time.minutesAgo', { count: diffInMinutes });
    } else if (diffInMinutes < 1440) { // less than 24 hours
      const diffInHours = Math.floor(diffInMinutes / 60);
      return t('notifications.time.hoursAgo', { count: diffInHours });
    } else {
      const diffInDays = Math.floor(diffInMinutes / 1440);
      return t('notifications.time.daysAgo', { count: diffInDays });
    }
  };


  return (
    <div className="notifications-overlay" onClick={handleOverlayClick}>
      <div className="notifications-modal" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
        <div className="modal-header">
          <h2>{t('notifications.title')}</h2>
          <div className="close" onClick={onClose}>&times;</div>
        </div>

        <div className="notifications-list">
          {(() => {
            // Combine and sort notifications and offer approvals
            // Fix 4: Filter out job approvals - they don't belong in Applicant view
            const offerApprovals = pendingApprovals.filter(a => a.type === 'offer_letter');

            const allItems = [
              ...notifications.map(n => ({ ...n, _isNotification: true, _timestamp: parseInt(n.timestamp) })),
              ...offerApprovals.map(a => ({ ...a, _isApproval: true, _timestamp: a.submittedAt }))
            ].sort((a, b) => b._timestamp - a._timestamp);

            if (allItems.length === 0) {
              return (
                <div className="no-notifications">
                  <i className="fa-solid fa-bell-slash"></i>
                  <p>{t('notifications.noNotifications')}</p>
                </div>
              );
            }

            return allItems.map(originalItem => {
              const item = originalItem._isApproval ? originalItem : localizeApplicantNotification(originalItem);
              if (item._isApproval) {
                return (
                  <ApprovalNotificationItem
                    key={item.id}
                    approval={item}
                    companyUID={companyUID}
                    onApprovalComplete={onApprovalComplete}
                    onViewDetails={onViewApprovalDetails}
                  />
                );
              }

              return (
                <div
                  key={item.id}
                  className="notification-item"
                  onClick={() => handleSelect(item)}
                >
                  <div className="notification-icon">
                    <i
                      className={getNotificationIcon(item.type)}
                      style={{ color: getNotificationColor(item.type) }}
                    />
                  </div>
                  <div className="notification-content">
                    <div className="notification-title">{item.title}</div>
                    <div className="notification-description">{item.description}</div>
                    <div className="notification-meta" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <span className="notification-candidate" style={{ fontWeight: 600, color: 'var(--app-heading, #031128)', fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.candidateName}</span>
                        <span className="notification-job" style={{ color: '#666', fontSize: 13, opacity: 0.85, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.jobTitle}</span>
                      </div>
                      <span className="notification-time" style={{ marginInlineStart: 16, color: '#666', fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0, textAlign: isRTL ? 'left' : 'right' }}>{formatTimestamp(item.timestamp)}</span>
                    </div>
                  </div>
                  {item.unread && <div className="notification-unread-indicator"></div>}
                </div>
              );
            });
          })()}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>{t('popup.close')}</button>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPopup; 